'use client';

import { createContext, useContext, useMemo, useState } from 'react';
import { RoomContext } from '@livekit/components-react';
import { APP_CONFIG_DEFAULTS, type AppConfig } from '@/app-config';
import { useRoom } from '@/hooks/useRoom';
import { useEmbedRoom } from '@/hooks/useEmbedRoom';

type SessionContextType = {
  appConfig: AppConfig;
  isSessionActive: boolean;
  startSession: () => void;
  endSession: () => void;
  // Timestamp (ms) after which chat messages are considered "new".
  chatResetTimestamp: number;
  // Clears the chat by advancing the reset timestamp to now
  clearChat: () => void;
};

const SessionContext = createContext<SessionContextType>({
  appConfig: APP_CONFIG_DEFAULTS,
  isSessionActive: false,
  startSession: async () => {},
  endSession: () => {},
  chatResetTimestamp: 0,
  clearChat: () => {},
});


type SessionMode = 'default' | 'embed';

interface SessionProviderProps {
  appConfig: AppConfig;
  children: React.ReactNode;
  mode?: SessionMode;
}

export const SessionProvider = ({ appConfig, children, mode = 'default' }: SessionProviderProps) => {
  const roomHook = mode === 'embed' ? useEmbedRoom : useRoom;
  const { room, isSessionActive, startSession, endSession } = roomHook(appConfig);
  const [chatResetTimestamp, setChatResetTimestamp] = useState<number>(0);

  const clearChat = () => setChatResetTimestamp(Date.now());

  const contextValue = useMemo(
    () => ({ appConfig, isSessionActive, startSession, endSession, chatResetTimestamp, clearChat }),
    [appConfig, isSessionActive, startSession, endSession, chatResetTimestamp]
  );

  return (
    <RoomContext.Provider value={room}>
      <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>
    </RoomContext.Provider>
  );
};

export function useSession() {
  return useContext(SessionContext);
}
